import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useSubscription, type SubscriptionTier } from "./useSubscription";

interface SubscriptionStatusData {
  subscribed: boolean;
  tier: SubscriptionTier;
  subscription_end: string | null;
  is_override: boolean;
  is_super_admin: boolean;
  is_test_account: boolean;
  is_canceling: boolean;
}

export function useSubscriptionStatus() {
  const { session } = useAuth();

  const {
    data: status,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["subscription-status", session?.user?.id],
    queryFn: async (): Promise<SubscriptionStatusData> => {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) {
        console.error("Error checking subscription:", error);
        return {
          subscribed: false,
          tier: "free",
          subscription_end: null,
          is_override: false,
          is_super_admin: false,
          is_test_account: false,
          is_canceling: false,
        };
      }
      return {
        subscribed: data?.subscribed ?? false,
        tier: (data?.tier as SubscriptionTier) ?? "free",
        subscription_end: data?.subscription_end ?? null,
        is_override: data?.is_override ?? false,
        is_super_admin: data?.is_super_admin ?? false,
        is_test_account: data?.is_test_account ?? false,
        is_canceling: data?.is_canceling ?? false,
      };
    },
    enabled: !!session,
    refetchInterval: 5 * 60_000, // 5 minuten — was 60 seconden
    staleTime: 4 * 60_000, // 4 minuten — was 30 seconden
  });

  const effectiveTier = status?.tier ?? "free";
  const subscription = useSubscription(effectiveTier);

  return {
    ...subscription,
    subscriptionEnd: status?.subscription_end ?? null,
    isOverride: status?.is_override ?? false,
    isSuperAdmin: status?.is_super_admin ?? false,
    isTestAccount: status?.is_test_account ?? false,
    isCanceling: status?.is_canceling ?? false,
    isLoading,
    refetch,
  };
}
